import {
  Button,
  Box,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Multiselect,
  MultiselectProps,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Textarea,
} from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import type { RuleInput, InputSource, UpdateRuleInput, Severity, RuleStatus, ExpressionLanguage } from '@amzn/helianthus-arbiter-client';
import { useArbiterClient } from '../../context/ArbiterClientContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

type WidenedExpressionLanguage = ExpressionLanguage | 'llm' | 'zelkova';

const SEVERITY_OPTIONS: SelectProps.Option[] = [
  { label: 'Critical', value: 'CRITICAL' },
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
];

const STATUS_OPTIONS: SelectProps.Option[] = [
  { label: 'Draft', value: 'draft' },
  { label: 'Dry Run', value: 'dry_run' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const LANGUAGE_OPTIONS: SelectProps.Option[] = [
  { label: 'Sangheli 1.0', value: 'sangheli-1.0' },
  { label: 'LLM', value: 'llm' },
  { label: 'Zelkova', value: 'zelkova' },
];

const SOURCE_TYPE_OPTIONS: SelectProps.Option[] = [
  { label: 'Discovery', value: 'discovery' },
  { label: 'Arbiter', value: 'arbiter' },
  { label: 'Call', value: 'call' },
  { label: 'Rule', value: 'rule' },
];

const RESOURCE_TYPE_OPTIONS: MultiselectProps.Option[] = [
  { label: 'AWS EC2 Instance', value: 'aws::ec2::instance' },
  { label: 'AWS S3 Bucket', value: 'aws::s3::bucket' },
  { label: 'AWS Lambda Function', value: 'aws::lambda::function' },
  { label: 'AWS IAM Role', value: 'aws::iam::role' },
  { label: 'AWS RDS Instance', value: 'aws::rds::instance' },
  { label: 'Latest Code Artifacts', value: 'AMZN::Code::LatestArtifacts' },
];

interface InputFormState {
  name: string;
  type: string;
  sourceType: string;
  discoveryType?: string;
  discoveryFilter?: string;
  arbiterKey?: string;
  callKey?: string;
  ruleTarget?: string;
}

function ruleInputToFormState(input: RuleInput): InputFormState {
  const state: InputFormState = {
    name: input.name ?? '',
    type: input.type ?? '',
    sourceType: 'discovery',
  };
  if (!input.source) return state;
  if (input.source.discovery) {
    state.sourceType = 'discovery';
    state.discoveryType = input.source.discovery.discoveryType ?? 'DESCRIBE_RESOURCE';
    state.discoveryFilter = input.source.discovery.filter?.expression ?? undefined;
  } else if (input.source.arbiter) {
    state.sourceType = 'arbiter';
    state.arbiterKey = input.source.arbiter.key;
  } else if (input.source.call) {
    state.sourceType = 'call';
    state.callKey = input.source.call.key;
  } else if (input.source.rule) {
    state.sourceType = 'rule';
    state.ruleTarget = input.source.rule.target;
  }
  return state;
}

export function EditRulePage() {
  const { ruleName } = useParams<{ ruleName: string }>();
  useDocumentTitle(ruleName ? `Edit Rule: ${ruleName}` : 'Edit Rule');
  const navigate = useNavigate();
  const client = useArbiterClient();

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [expectedVersion, setExpectedVersion] = useState('');

  // Form fields
  const [description, setDescription] = useState('');
  const [appliesTo, setAppliesTo] = useState<MultiselectProps.Option[]>([]);
  const [severity, setSeverity] = useState<SelectProps.Option | null>(null);
  const [ruleStatus, setRuleStatus] = useState<SelectProps.Option | null>(null);
  const [inputs, setInputs] = useState<InputFormState[]>([]);
  const [language, setLanguage] = useState<SelectProps.Option | null>(LANGUAGE_OPTIONS[0]);
  const [expression, setExpression] = useState('');

  useEffect(() => {
    if (!ruleName) return;
    setLoading(true);
    client.describeRule({ ruleName })
      .then((res) => {
        const rule = res.rule!;
        setExpectedVersion(rule.version ?? '');
        setDescription(rule.description || '');
        setAppliesTo(
          (rule.appliesTo ?? []).map(rt => {
            const match = RESOURCE_TYPE_OPTIONS.find(o => o.value === rt);
            return match || { label: rt, value: rt };
          })
        );
        setSeverity(SEVERITY_OPTIONS.find(o => o.value === rule.severity) || null);
        setRuleStatus(STATUS_OPTIONS.find(o => o.value === rule.ruleStatus) || STATUS_OPTIONS[0]);
        setInputs((rule.inputs ?? []).map(ruleInputToFormState));
        setLanguage(LANGUAGE_OPTIONS.find(o => o.value === rule.evaluation?.language) || LANGUAGE_OPTIONS[0]);
        setExpression(rule.evaluation?.expression ?? '');
      })
      .catch((err) => setFetchError(err.message))
      .finally(() => setLoading(false));
  }, [ruleName, client]);

  const addInput = () => {
    setInputs([...inputs, { name: '', type: '', sourceType: 'discovery' }]);
  };

  const removeInput = (index: number) => {
    setInputs(inputs.filter((_, i) => i !== index));
  };

  const updateInput = (index: number, field: keyof InputFormState, value: string) => {
    const updated = [...inputs];
    updated[index] = { ...updated[index], [field]: value };
    setInputs(updated);
  };

  const convertInputs = (): RuleInput[] => {
    return inputs.map(input => {
      let source: InputSource;
      if (input.sourceType === 'discovery') {
        source = { discovery: { discoveryType: input.discoveryType || 'DESCRIBE_RESOURCE', filter: input.discoveryFilter ? { language: 'yaql', expression: input.discoveryFilter } : undefined } } as InputSource.DiscoveryMember;
      } else if (input.sourceType === 'arbiter') {
        source = { arbiter: { key: input.arbiterKey || '' } } as InputSource.ArbiterMember;
      } else if (input.sourceType === 'call') {
        source = { call: { key: input.callKey || '' } } as InputSource.CallMember;
      } else {
        source = { rule: { target: input.ruleTarget || '' } } as InputSource.RuleMember;
      }
      return { name: input.name, source, type: input.type };
    });
  };

  const handleSubmit = async () => {
    if (!ruleName) return;
    setSubmitError(null);
    setSubmitting(true);

    try {
      const request: UpdateRuleInput = {
        ruleName,
        expectedVersion,
        description: description || undefined,
        appliesTo: appliesTo.map(opt => opt.value || ''),
        inputs: convertInputs(),
        evaluation: {
          language: (language?.value as WidenedExpressionLanguage as ExpressionLanguage) || 'sangheli-1.0',
          expression,
        },
        severity: severity?.value as Severity | undefined,
        ruleStatus: ruleStatus?.value as RuleStatus | undefined,
      };

      await client.updateRule(request);
      navigate(`/rules/${ruleName}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Update failed';
      if (msg.includes('409') || msg.toLowerCase().includes('conflict')) {
        setSubmitError('This rule was modified by another user. Please refresh and try again.');
      } else {
        setSubmitError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Box textAlign="center" padding={{ top: 'xxxl' }}><Spinner size="large" /></Box>;
  }

  if (fetchError) {
    return <Container><StatusIndicator type="error">{fetchError}</StatusIndicator></Container>;
  }

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Edit Rule: {ruleName}</Header>

      {submitError && (
        <Box color="text-status-error" padding="s">{submitError}</Box>
      )}

      <Container header={<Header variant="h2">Basic Information</Header>}>
        <SpaceBetween size="l">
          <FormField label="Rule Name">
            <Input value={ruleName || ''} disabled={true} />
          </FormField>

          <FormField label="Version (optimistic lock)">
            <Input value={expectedVersion} disabled={true} />
          </FormField>

          <FormField label="Description">
            <Textarea
              value={description}
              onChange={({ detail }) => setDescription(detail.value)}
              rows={3}
            />
          </FormField>

          <FormField label="Applies To" constraintText="At least one required">
            <Multiselect
              selectedOptions={appliesTo}
              onChange={({ detail }) => setAppliesTo([...detail.selectedOptions])}
              options={RESOURCE_TYPE_OPTIONS}
              filteringType="auto"
            />
          </FormField>

          <ColumnLayout columns={2}>
            <FormField label="Severity">
              <Select
                selectedOption={severity}
                onChange={({ detail }) => setSeverity(detail.selectedOption)}
                options={SEVERITY_OPTIONS}
              />
            </FormField>
            <FormField label="Status">
              <Select
                selectedOption={ruleStatus}
                onChange={({ detail }) => setRuleStatus(detail.selectedOption)}
                options={STATUS_OPTIONS}
              />
            </FormField>
          </ColumnLayout>
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header variant="h2" actions={<Button onClick={addInput}>Add Input</Button>}>
            Inputs
          </Header>
        }
      >
        <SpaceBetween size="l">
          {inputs.length === 0 ? (
            <Box textAlign="center" color="text-body-secondary">No inputs defined.</Box>
          ) : (
            inputs.map((input, index) => (
              <Container
                key={index}
                header={
                  <Header variant="h3" actions={<Button onClick={() => removeInput(index)} variant="link">Remove</Button>}>
                    Input {index + 1}
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  <ColumnLayout columns={2}>
                    <FormField label="Name"><Input value={input.name} onChange={({ detail }) => updateInput(index, 'name', detail.value)} /></FormField>
                    <FormField label="Type"><Input value={input.type} onChange={({ detail }) => updateInput(index, 'type', detail.value)} /></FormField>
                  </ColumnLayout>
                  <FormField label="Source Type">
                    <Select
                      selectedOption={SOURCE_TYPE_OPTIONS.find(opt => opt.value === input.sourceType) || SOURCE_TYPE_OPTIONS[0]}
                      onChange={({ detail }) => updateInput(index, 'sourceType', detail.selectedOption.value || 'discovery')}
                      options={SOURCE_TYPE_OPTIONS}
                    />
                  </FormField>
                  {input.sourceType === 'discovery' && (
                    <>
                      <FormField label="Discovery Type"><Input value={input.discoveryType || 'DESCRIBE_RESOURCE'} onChange={({ detail }) => updateInput(index, 'discoveryType', detail.value)} /></FormField>
                      <FormField label="Filter"><Input value={input.discoveryFilter || ''} onChange={({ detail }) => updateInput(index, 'discoveryFilter', detail.value)} /></FormField>
                    </>
                  )}
                  {input.sourceType === 'arbiter' && <FormField label="Key"><Input value={input.arbiterKey || ''} onChange={({ detail }) => updateInput(index, 'arbiterKey', detail.value)} /></FormField>}
                  {input.sourceType === 'call' && <FormField label="Key"><Input value={input.callKey || ''} onChange={({ detail }) => updateInput(index, 'callKey', detail.value)} /></FormField>}
                  {input.sourceType === 'rule' && <FormField label="Target"><Input value={input.ruleTarget || ''} onChange={({ detail }) => updateInput(index, 'ruleTarget', detail.value)} /></FormField>}
                </SpaceBetween>
              </Container>
            ))
          )}
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Evaluation</Header>}>
        <SpaceBetween size="l">
          <FormField label="Language">
            <Select selectedOption={language} onChange={({ detail }) => setLanguage(detail.selectedOption)} options={LANGUAGE_OPTIONS} />
          </FormField>
          <FormField label="Expression" constraintText="Required">
            <Textarea value={expression} onChange={({ detail }) => setExpression(detail.value)} rows={10} />
          </FormField>
        </SpaceBetween>
      </Container>

      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => navigate(`/rules/${ruleName}`)}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} loading={submitting}>Save changes</Button>
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  );
}
